import { AnimatePresence, motion } from 'motion/react';
import React, { useMemo, useRef, useState } from 'react';

import { type TransferReviewBatch, TransferReviewBatchDecision, type UnifiedTransfer } from '@column/api';
import { Button, Input } from '@column/column-ui-kit';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle, Label } from '@column/ui';

import { getReviewTotals, getReviewTypeCounts } from '../_utils/reviewTotals';
import { getErrorMessage } from '~/lib/api/getErrorMessage';
import { AnimatedHeight, BACKGROUND_NOTICE, EASE, ReviewDialog } from '~/lib/approvals/components/ReviewDialog';
import { type ReviewTargets, useCreateReviewBatch } from '~/lib/transfers/hooks/useCreateReviewBatch';
import { pluralizeTransfers } from '~/lib/transfers/utils';
import { isStepUpRequiredError } from '~/repositories/StepUpRepository';
import { useNotificationStore } from '~/stores/Notification';

import { ReviewBatchHeader } from './ReviewBatchHeader';
import { ReviewBatchSummary } from './ReviewBatchSummary';

const COMMENT_MAX_LENGTH = 1023;

export interface ReviewBatchRequest {
  decision: TransferReviewBatchDecision;
  transferCount: number;
  targets: ReviewTargets;
  onOptimisticSubmit: (targets: ReviewTargets) => void;
  onSubmitFailed: () => void;
  lookupTransfers: (ids: string[]) => Record<string, UnifiedTransfer>;
}

interface SubmittedReview {
  request: ReviewBatchRequest;
  reviewBatchId?: string;
  knownTransfers?: Record<string, UnifiedTransfer>;
  error?: string;
}

const getReviewTitle = (decision: TransferReviewBatchDecision, count: number): string =>
  `${decision === TransferReviewBatchDecision.APPROVED ? 'Approve' : 'Reject'} ${pluralizeTransfers(count)}`;

interface ReviewBatchDialogProps {
  request?: ReviewBatchRequest;
  onClose: () => void;
}

export const ReviewBatchDialog: React.FC<ReviewBatchDialogProps> = ({ request, onClose }) => {
  const { addDangerNotification } = useNotificationStore();
  const createReviewBatch = useCreateReviewBatch();
  const [shown, setShown] = useState(request);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState<SubmittedReview>();
  const [isWide, setIsWide] = useState(false);
  const requestRef = useRef(request);
  requestRef.current = request;

  if (request && request !== shown) {
    setShown(request);
    setComment('');
    setSubmitted(undefined);
    setIsWide(false);
  }

  const handleSubmit = async () => {
    if (!request) return;
    const { decision, targets, onOptimisticSubmit, onSubmitFailed, lookupTransfers } = request;
    const update = (partial: Partial<SubmittedReview>) =>
      setSubmitted((prev) => (prev?.request === request ? { ...prev, ...partial } : prev));

    setSubmitted({ request, knownTransfers: lookupTransfers(targets.transfer_ids) });
    onOptimisticSubmit(targets);

    let batch: TransferReviewBatch;
    try {
      batch = await createReviewBatch({ decision, targets, comment });
    } catch (error) {
      if (isStepUpRequiredError(error)) {
        onSubmitFailed();
        if (requestRef.current === request) onClose();
        return;
      }
      const message = getErrorMessage(error);
      addDangerNotification({ content: message });
      update({ error: message });
      onSubmitFailed();
      return;
    }
    update({ reviewBatchId: batch.id });
  };

  const reviewRows = useMemo(() => {
    if (!shown) return undefined;
    const ids = [...shown.targets.transfer_ids, ...shown.targets.ach_transfer_batch_ids];
    const found = Object.values(shown.lookupTransfers(ids));
    return found.length === ids.length ? found : undefined;
  }, [shown]);
  const totals = useMemo(() => (reviewRows ? getReviewTotals(reviewRows) : undefined), [reviewRows]);
  const typeCounts = useMemo(() => (reviewRows ? getReviewTypeCounts(reviewRows) : undefined), [reviewRows]);

  const shownDecision = shown?.decision ?? TransferReviewBatchDecision.APPROVED;
  const transferCount = shown?.transferCount ?? 0;
  const isApprove = shownDecision === TransferReviewBatchDecision.APPROVED;
  const subject = transferCount === 1 ? 'The transfer' : 'The transfers';
  const description = isApprove
    ? `${subject} will be initiated.`
    : `${subject} will be marked as rejected, no funds will be withdrawn.`;

  return (
    <ReviewDialog isOpen={Boolean(request)} isWide={isWide} onClose={onClose}>
      <DialogHeader>
        <DialogTitle>{getReviewTitle(shownDecision, transferCount)}</DialogTitle>
        <DialogDescription>{submitted && !submitted.error ? BACKGROUND_NOTICE : description}</DialogDescription>
      </DialogHeader>
      {shown && <ReviewBatchHeader totals={totals} typeCounts={typeCounts} />}
      <AnimatedHeight className="grid gap-4">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={submitted ? 'summary' : 'confirm'}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8, transition: { duration: 0.12, ease: EASE } }}
            transition={{ duration: 0.2, ease: EASE, delay: submitted ? 0.12 : 0 }}
            className="grid min-w-0 gap-4"
          >
            {submitted ? (
              <ReviewBatchSummary
                decision={shownDecision}
                requestedCount={transferCount}
                reviewBatchId={submitted.reviewBatchId}
                error={submitted.error}
                knownTransfers={submitted.knownTransfers}
                onHasAttentionChange={setIsWide}
              />
            ) : (
              <div className="grid gap-2">
                <Label>Comment</Label>
                <Input
                  value={comment}
                  placeholder="Add a comment..."
                  onChange={(value) => setComment(value.slice(0, COMMENT_MAX_LENGTH))}
                />
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </AnimatedHeight>
      {!submitted && (
        <DialogFooter className="flex-row items-center justify-between sm:justify-between">
          <Button variant="secondary" size="small" onClick={onClose}>
            Cancel
          </Button>
          <Button variant={isApprove ? 'primary' : 'danger'} size="small" onClick={handleSubmit}>
            {isApprove ? 'Approve' : 'Reject'}
          </Button>
        </DialogFooter>
      )}
    </ReviewDialog>
  );
};
